const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB

// 1) Initiate resumable session using the signed POST URL from your API
export async function InitiateGcsResumable(
    initSignedUrl: string,
    file: Blob,
    contentType?: string
): Promise<string> {
    const res = await fetch(initSignedUrl, {
        method: "POST",
        headers: {
            "x-goog-resumable": "start",
            "Content-Type": contentType || (file.type || "application/octet-stream"),
        },
    });
    if (res.status !== 201) throw new Error(`Init failed: ${res.status}`);
    const sessionUrl = res.headers.get("Location");
    if (!sessionUrl) throw new Error("No session URL returned");
    return sessionUrl;
}

export async function GetGcsServerOffset(sessionUrl: string, total: number): Promise<number> {
    const res = await fetch(sessionUrl, {
        method: "PUT",
        headers: { "Content-Range": `bytes */${total}` },
    });
    // GCS returns 308 with "Range: bytes=0-<lastByteIndex>"
    if (res.status === 308) {
        const range = res.headers.get("Range"); // e.g., "bytes=0-10485759"
        return range ? Number(range.split("-")[1]) + 1 : 0;
    }
    if (res.ok) return total; // somehow completed
    const err = new Error(`Probe failed: ${res.status}`);
    // @ts-ignore
    err.status = res.status;
    throw err;
}

export async function UploadGcsResumable(
    sessionUrl: string,
    file: Blob,
    onProgress?: (pct: number) => Promise<boolean>,
    startAt = 0,                              // resume offset if you have it
    contentType?: string,
): Promise<void> {
    let offset = startAt;

    // If we don't trust startAt (e.g., after long pause), re-probe:
    if (offset > 0) {
        try { offset = await GetGcsServerOffset(sessionUrl, file.size); } catch {}
    } else {
        offset = await GetGcsServerOffset(sessionUrl, file.size); // start fresh or resume
    }

    while (offset < file.size) {
        const end = Math.min(offset + CHUNK_SIZE, file.size);
        const chunk = file.slice(offset, end);

        const headers = new Headers();
        headers.set("Content-Range", `bytes ${offset}-${end - 1}/${file.size}`);
        headers.set("Content-Type", contentType || (file.type || "application/octet-stream"));

        const res = await fetch(sessionUrl, { method: "PUT", headers, body: chunk } as RequestInit);

        if (res.status === 308) {
            // Partial; continue from server-confirmed offset
            const range = res.headers.get("Range");
            offset = range ? Number(range.split("-")[1]) + 1 : end;
        } else if (res.ok) {
            // 200/201 => done
            offset = file.size;
        } else {
            const err = new Error(`Chunk failed: ${res.status}`);
            // @ts-ignore
            err.status = res.status;
            throw err;
        }

        if (onProgress) {
            const trackExists = await onProgress((offset / file.size) * 100);
            if (!trackExists) {
                console.log("Track does not exist, stopping upload")
                break;
            }
        }
    }
}
